/**
 * Utilitário central para identificar bloqueios/advertências emitidos pelo
 * gatilho declarativo do Workflow (`workflow_tg_guard`).
 *
 * O backend informa o marcador estruturado `WORKFLOW_GUARD_DENY` no campo HINT
 * e um JSON mínimo em DETAIL. As expressões legadas abaixo existem apenas para
 * compatibilidade com ambientes que ainda não receberam a migration v2.2.0.
 *
 * RELEASE: 2026-09-03 v1.0.0 - Criação do detector estruturado de feedback do Workflow.
 */

export const WORKFLOW_GUARD_HINT = 'WORKFLOW_GUARD_DENY';
export const WORKFLOW_GUARD_ORIGEM = 'workflow_guard';

export type WorkflowGuardPolicy = 'ALLOW' | 'WARN' | 'DENY' | null;

interface WorkflowGuardErrorLike {
  message?: unknown;
  hint?: unknown;
  details?: unknown;
  code?: unknown;
  error?: unknown;
}

/** Extrai um objeto simples de erro, inclusive quando o SDK encapsula em `error`. */
function extrairErro(error: unknown): WorkflowGuardErrorLike {
  if (!error || typeof error !== 'object') return {};
  const erro = error as WorkflowGuardErrorLike;

  // Alguns fluxos encapsulam o erro original em `error.error`.
  if (erro.error && typeof erro.error === 'object') {
    return { ...extrairErro(erro.error), ...erro };
  }

  return erro;
}

/** Converte DETAIL em objeto quando o PostgREST o entrega como string JSON. */
function detalhesComoObjeto(details: unknown): Record<string, unknown> | null {
  if (!details) return null;
  if (typeof details === 'object' && !Array.isArray(details)) {
    return details as Record<string, unknown>;
  }
  if (typeof details !== 'string') return null;

  try {
    const parsed = JSON.parse(details);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
}

/** Normaliza a mensagem para exibição segura e compacta em UI. */
export function getWorkflowGuardMessage(error: unknown): string {
  const erro = extrairErro(error);
  const mensagem = typeof erro.message === 'string'
    ? erro.message
    : typeof error === 'string'
      ? error
      : '';

  const normalizada = mensagem.replace(/\s+/g, ' ').trim();
  if (!normalizada) {
    return 'Existe uma pendência do processo que precisa ser resolvida antes de continuar.';
  }

  // Limite defensivo para evitar que um payload inesperado quebre o modal.
  return normalizada.length > 700 ? `${normalizada.slice(0, 697)}...` : normalizada;
}

/** Retorna a política informada pelo DETAIL estruturado, quando disponível. */
export function getWorkflowGuardPolicy(error: unknown): WorkflowGuardPolicy {
  const detalhes = detalhesComoObjeto(extrairErro(error).details);
  const resultado = detalhes?.resultado;
  return resultado === 'ALLOW' || resultado === 'WARN' || resultado === 'DENY'
    ? resultado
    : null;
}

/**
 * Identifica se o erro representa uma trava do Workflow.
 *
 * A verificação prioriza o marcador estruturado da migration v2.2.0. O fallback
 * por texto é intencionalmente restrito às mensagens conhecidas da trava para
 * não reclassificar erros comuns de permissão como feedback de processo.
 */
export function isWorkflowGuardError(error: unknown): boolean {
  const erro = extrairErro(error);
  const hint = typeof erro.hint === 'string' ? erro.hint.trim().toUpperCase() : '';
  const detalhes = detalhesComoObjeto(erro.details);

  if (hint === WORKFLOW_GUARD_HINT) return true;
  if (detalhes?.origem === WORKFLOW_GUARD_ORIGEM) return true;

  const mensagem = getWorkflowGuardMessage(error).toLocaleLowerCase('pt-BR');
  const codigo = typeof erro.code === 'string' ? erro.code : '';

  // Compatibilidade com a resposta textual emitida antes da migration v2.2.0.
  const mensagemConhecida =
    mensagem.includes('ação bloqueada pelo processo') ||
    mensagem.includes('acao bloqueada pelo processo') ||
    mensagem.includes('existe uma exigência ou aprovação em aberto') ||
    mensagem.includes('existe pendência de aprovação em aberto');

  if (mensagemConhecida) return true;

  // Fallback conservador para mensagens customizadas configuradas no Workflow.
  return (
    codigo === '42501' &&
    mensagem.includes('processo') &&
    (mensagem.includes('aprov') || mensagem.includes('bloque'))
  );
}
